import { useEffect, useMemo } from 'react';
import { useCookies } from 'next-client-cookies';
import { useTranslations } from 'use-intl';
import { DownArrow, FilterIconLightMobile } from 'components/icons';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { mediumDesktop } from 'helpers/utils/screensizes';
import { getLocalizationInfo } from 'project.config';
import { useCart } from 'frontastic';
import { RetailPickUp } from './Retailpickup';
import { useProductList } from '../../context';
import styles from '../../styles/index.module.css';
import { SortConfiguration } from '../../types';
import SortFacet from '../facets/sort';

export interface DesktopFacetsProps {
  sortConfiguration?: SortConfiguration[];
  openFacets?: boolean;
  setOpenFacets?: any;
  pickUpToday?: string;
  filterLabel?: string;
  sortbyLabel?: string;
}
const DesktopFacets: React.FC<DesktopFacetsProps> = ({
  sortConfiguration,
  openFacets,
  setOpenFacets,
  pickUpToday,
  filterLabel,
}) => {
  const translate = useTranslations();
  const [isDesktopSize] = useMediaQuery(mediumDesktop);
  const { isContainsP2RItems } = useProductList();
  const cookies = useCookies();
  const { shippingMethodsData } = useCart();
  const locale = cookies.get('locale') as string;

  const { countryCode } = getLocalizationInfo(locale);

  const showP2R = (shippingMethodsData?.data ?? []).some((method) =>
    method.shippingMethodKey?.toLowerCase().startsWith('p2r'),
  );

  const sortFacet = useMemo(
    () => (
      <div className="sortDropdown flex w-full items-center justify-end gap-4 lg:w-auto">
        <SortFacet sortConfiguration={sortConfiguration} />
      </div>
    ),
    [translate],
  );
  useEffect(() => {
    if (isDesktopSize) setOpenFacets(true);
  }, [isDesktopSize]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const next = !openFacets;
    setOpenFacets(next);

    // Keep focus on button irrespective of facet state to ensure accessibility
    const btn = e.currentTarget;
    requestAnimationFrame(() => {
      btn.focus();
    });
  };

  return (
    <div className="mx-20 mt-32 hidden items-center justify-between border-y-2 border-[#EBEBEB] py-12 md:flex">
      <div className="flex min-w-fit items-center" data-ffid="ctr-filters-sort-desktop">
        {isDesktopSize ? (
          <>
            <button
              data-ffid="btn-filters-desktop"
              tabIndex={0}
              aria-expanded={openFacets}
              aria-controls="filters-panel"
              onClick={handleClick}
              className={`mr-40 flex min-w-240 justify-between rounded-md border border-solid bg-white px-12 py-8 font-normal hover:cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0066CC] focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                openFacets ? styles.facet_dropdown_selected : styles.facet_dropdown_unselected
              }`}
            >
              <div className="flex items-center gap-8">
                <FilterIconLightMobile className="text-red" />
                <span>{filterLabel}</span>
              </div>

              <DownArrow
                className={`w-24 text-[#333333] transition-transform duration-300 dark:text-white ${
                  openFacets ? 'rotate-180' : 'rotate-0'
                }`}
                data-ffid="icon-filter-down-arrow"
              />
            </button>

            <span className="sr-only" aria-live="assertive">
              {openFacets ? 'Filters expanded' : ''}
            </span>
          </>
        ) : (
          <button
            data-ffid={`btn-filters-mobile`}
            onClick={() => {
              setOpenFacets(!openFacets);
            }}
            className={`flex rounded-[34px] px-14 py-12 text-14 text-white ${styles.facet_button}`}
          >
            <FilterIconLightMobile className="pt-4" /> {filterLabel}
          </button>
        )}

        {countryCode === 'US' && isContainsP2RItems && showP2R && <RetailPickUp pickUpToday={pickUpToday} />}
      </div>
      <div className="flex-1 items-center gap-18" data-ffid="ctr-sort-desktop">
        <div>{sortFacet}</div>
      </div>
    </div>
  );
};

export default DesktopFacets;
